/*
 * CustomersDashboard.tsx
 */

// Imports React

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AddButton, DeleteButton, MainButton } from '../common';
import {
	TiersData,
	deleteTiers,
	TiersTypeData,
	tiersApi,
} from 'features/tiers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faBuilding,
	faPen,
	faTrashAlt,
	faFileContract,
	faAngleRight,
} from '@fortawesome/free-solid-svg-icons';
import { QuoteData } from 'features/quotes/types/types';
import { quotesApi } from 'features/quotes/services/quotesApi';
import { ProjectsCrudCard } from 'features/projects/components/ProjectsCrudCard';
import ActivitiesCrudCard from 'features/activities/components/ActivityCrudCard';
import { LogisticElementsCrudCard } from 'features/logisticElements/components/LogisticElementsCrudCard';
import { CustomerOrdersCrudCard } from 'features/customerOrders/components/CustomerOrdersCrudCard';
import { CustomerInvoicesCrudCard } from 'features/customerInvoices/components/CustomerInvoicesCrudCard';
import { AdvancesCrudCard } from 'features/advances/components/AdvancesCrudCard';
import { CreditNotesCrudCard } from 'features/creditNotes/components/CreditNotesCrudCard';

// CustomersDashboard page component
const CustomersDashboard: React.FC = () => {
	const isCustomerView = true;
	const isSupplierView = false;

	// State management
	const [loading, setLoading] = useState(true);
	const [tiers, setTiers] = useState<TiersData[]>([]);
	const navigate = useNavigate();
	const [quotes, setQuotes] = useState<QuoteData[]>([]);

	// Fetch tiers on component mount
	useEffect(() => {
		// Get token from localStorage
		const token = localStorage.getItem('token');
		// If no token, exit
		if (!token) return;
		// Fetch tiers from API
		tiersApi
			.listPaginated(token, {
				sort: 'label',
				order: 'ASC',
				limit: 999999,
				filters: isCustomerView
					? { 'types.code': 'CLI' }
					: { 'types.code': 'FOU' },
			})
			.then((res) => setTiers(res.data))
			.catch((error) => console.error(error))
			.finally(() => setLoading(false));
	}, []);

	useEffect(() => {
		// Get token from localStorage
		const token = localStorage.getItem('token');
		// If no token, exit
		if (!token) return;
		// Fetch quotes from API
		quotesApi
			.listPaginated(token, { limit: 999999 })
			.then((res) => setQuotes(res.data))
			.finally(() => setLoading(false));
	}, []);

	// Handlers for add, edit, and delete actions
	const handleAdd = () => {
		if (isCustomerView) {
			navigate('/app/tiers/new?roles=client');
		} else if (isSupplierView) {
			navigate('/app/tiers/new?roles=fournisseur');
		}
	};
	const handleManage = (id: number) => navigate(`/app/tiers/${id}/manage`);
	const handleDelete = (id: number) => {
		const token = localStorage.getItem('token');
		if (!token) return;

		deleteTiers(id, token)
			.then(() => {
				setTiers((prev) => prev.filter((tiers) => tiers.id !== id));
			})
			.catch((error) => console.error(error));
	};

	const isClient = (t: TiersTypeData[]): boolean => {
		return t?.some((type) => type.code === 'CLI');
	};

	const isSupplier = (t: TiersTypeData[]): boolean => {
		return t?.some((type) => type.code === 'FOU');
	};

	const handleAddQuote = () => {
		navigate('/app/quotes/new');
	};

	const handleManageQuote = (id: number) =>
		navigate(`/app/quotes/${id}/manage`);

	const handleDeleteQuote = (id: number) => {
		const token = localStorage.getItem('token');
		if (!token) return;

		quotesApi
			.delete(id, token)
			.then(() => {
				setQuotes((prev) => prev.filter((quote) => quote.id !== id));
			})
			.catch((error) => console.error(error));
	};

	// Rendu du composant CustomersDashboard
	return (
		<div>
			{/* Ancre avec le nom de la catégorie */}
			<a id="commercial-relationship">
				<h1 className="text-3xl font-medium mr-2 mb-6 mt-4">
					Relation commerciale
				</h1>
			</a>

			{/* CARD Contenant les informations des devis */}
			<div className=" p-6 bg-white rounded-lg shadow-md mb-6">
				{/* HEADER DE LA CARD */}
				<div className="flex mb-3 text-gray-700 items-center">
					<FontAwesomeIcon icon={faFileContract} className="w-6 h-6 mr-2" />
					<h2 className="text-2xl font-bold">Devis</h2>
					<div className="ml-auto">
						<AddButton label="" onClick={handleAddQuote} size="sm" />
					</div>
				</div>

				{loading ? (
					<p>Chargement des devis...</p>
				) : (
					<div className="overflow-x-auto">
						{/* Table structure with headers and rows for each order */}
						<table className="min-w-full border border-gray-200 rounded-lg">
							{/* Table headers */}
							<thead className="bg-gray-50">
								<tr>
									<th className="px-6 py-3 text-left text-gray-700 font-medium border-b">
										Référence
									</th>
									<th className="px-6 py-3 text-left text-gray-700 font-medium border-b">
										Client
									</th>
									<th className="px-6 py-3 text-left text-gray-700 font-medium border-b">
										Montant
									</th>
									<th className="px-6 py-3 text-left text-gray-700 font-medium border-b">
										Statut
									</th>
									<th className="px-6 py-3 text-left text-gray-700 font-medium border-b">
										Actions
									</th>
								</tr>
							</thead>
							{/* Table body */}
							<tbody className="bg-white divide-y divide-gray-200">
								{quotes.map((quote) => {
									const lastVersion =
										quote.quoteVersions?.[quote.quoteVersions.length - 1];

									return (
										<tr key={quote.id}>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
												{quote.referenceNumber}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
												{quote.tiers?.label}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
												{(
													quote.quoteVersions?.[
														quote.quoteVersions.length - 1
													]?.quoteLines?.reduce(
														(acc, line) => acc + line.total,
														0
													) ?? 0
												).toLocaleString('fr-FR', {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												})}{' '}
												€
											</td>

											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
												{lastVersion?.status}
											</td>
											<td className="px-6 py-4 border-b space-x-2">
												<div className="flex space-x-2">
													{handleManageQuote && (
														<MainButton
															label=""
															onClick={() => handleManageQuote(quote.id)}
															size="sm"
															icon={faPen}
														/>
													)}
													{handleDeleteQuote && (
														<DeleteButton
															label=""
															question={`Voulez-vous vraiment supprimer le devis ${quote.id} ?`}
															onConfirm={() => handleDeleteQuote(quote.id)}
															size="sm"
															icon={faTrashAlt}
														/>
													)}
												</div>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</div>

			{/* Grille haute : 2 colonnes (1/2 et 1/2) */}
			<div className="grid gap-6 grid-cols-1 md:grid-cols-2 mb-6">
				{/* CARD Contenant les informations des clients */}
				<div className=" p-6 bg-white rounded-lg shadow-md">
					{/* HEADER DE LA CARD */}
					<div className="flex mb-3 text-gray-700 items-center">
						<FontAwesomeIcon icon={faBuilding} className="w-6 h-6 mr-2" />
						<h2 className="text-2xl font-bold">Clients</h2>
						<div className="ml-auto">
							<AddButton label="" onClick={handleAdd} size="sm" />
						</div>
					</div>
					{loading ? (
						isCustomerView ? (
							<p>Chargement des clients...</p>
						) : (
							<p>Chargement des fournisseurs...</p>
						)
					) : (
						<div className="overflow-x-auto">
							{/* Table structure with headers and rows for each tiers */}
							<table className="min-w-full border border-gray-200 rounded-lg">
								{/* Table headers */}
								<thead className="bg-gray-50">
									<tr>
										<th className="px-6 py-3 text-left text-gray-700 font-medium border-b">
											Label
										</th>
										<th className="px-6 py-3 text-left text-gray-700 font-medium border-b">
											Actions
										</th>
									</tr>
								</thead>
								{/* Table body */}
								<tbody className="bg-white divide-y divide-gray-200">
									{/* tiers.map((tier) et filtre sur isCustomerView et isSupplierView selon tier.type (fonction isClient et isSupplier)*/}
									{tiers
										.filter((tier) => {
											if (isCustomerView && !isSupplierView) {
												return isClient(tier.types ?? []);
											} else if (!isCustomerView && isSupplierView) {
												return isSupplier(tier.types ?? []);
											}
											return true;
										})
										.map((tier) => (
											<tr key={tier.id}>
												<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
													{tier.label}
												</td>
												<td className="px-6 py-4 border-b space-x-2">
													<div className="flex space-x-2">
														{handleManage && (
															<MainButton
																label=""
																onClick={() => handleManage(tier.id)}
																size="sm"
																icon={faPen}
															/>
														)}
														{handleDelete && (
															<DeleteButton
																label=""
																question={`Voulez-vous vraiment supprimer le tiers ${tier.label} ?`}
																onConfirm={() => handleDelete(tier.id)}
																size="sm"
																icon={faTrashAlt}
															/>
														)}
													</div>
												</td>
											</tr>
										))}
								</tbody>
							</table>
						</div>
					)}
				</div>

				{/* CARD Contenant les informations des commandes clients */}
				<CustomerOrdersCrudCard
					size="sm"
					returnPath="/app/customerOrders"
					returnLabel="Toutes les commandes clients"
					returnIcon={faAngleRight}
					returnInvert={true}
					className="w-full"
				/>
			</div>

			{/* Ancre avec le nom de la catégorie */}
			<a id="operational-management">
				<h1 className="text-3xl font-medium mr-2 mb-6 mt-16">
					Gestion opérationelle
				</h1>
			</a>

			{/* CARD Contenant les informations des affaires */}
			<ProjectsCrudCard
				size="sm"
				returnPath="/app/projects"
				returnLabel="Toutes les affaires"
				returnIcon={faAngleRight}
				returnInvert={true}
				className="w-full mb-6 mx-0 max-w-none"
			/>

			{/* Grille basse : 2 colonnes */}
			<div className="grid gap-6 grid-cols-1 md:grid-cols-2 mb-6">
				{/* CARD Contenant les informations des activités */}
				<ActivitiesCrudCard
					size="sm"
					returnPath="/app/activities"
					returnLabel="Toutes les activités"
					returnIcon={faAngleRight}
					returnInvert={true}
					className="w-full"
				/>

				{/* CARD Contenant les informations des éléments logistiques */}
				<LogisticElementsCrudCard
					size="sm"
					returnPath="/app/logisticElements"
					returnLabel="Tous les éléments logistiques"
					returnIcon={faAngleRight}
					returnInvert={true}
					className="w-full"
				/>
			</div>

			{/* Ancre avec le nom de la catégorie */}
			<a id="billing">
				<h1 className="text-3xl font-medium mr-2 mb-6 mt-16">Facturation</h1>
			</a>

			{/* Grille basse : 2 colonnes */}
			{/* CARD Contenant les informations des factures */}
			<CustomerInvoicesCrudCard
				size="sm"
				returnPath="/app/customerInvoices"
				returnLabel="Toutes les factures clients"
				returnIcon={faAngleRight}
				returnInvert={true}
				className="w-full mb-6 mx-0 max-w-none"
			/>
			{/* Grille basse : 2 colonnes */}
			<div className="grid gap-6 grid-cols-1 md:grid-cols-2 mb-6">
				{/* CARD Contenant les informations des acomptes */}
				<AdvancesCrudCard
					size="sm"
					returnPath="/app/advances"
					returnLabel="Tous les acomptes"
					returnIcon={faAngleRight}
					returnInvert={true}
					className="w-full"
				/>
				{/* CARD Contenant les informations des avoirs */}
				<CreditNotesCrudCard
					size="sm"
					returnPath="/app/creditNotes"
					returnLabel="Tous les avoirs"
					returnIcon={faAngleRight}
					returnInvert={true}
					className="w-full"
				/>
			</div>
		</div>
	);
};

export default CustomersDashboard;
